import React, { useEffect, useRef, useState } from 'react';
import { Input, Button, Tooltip, Space, message, Avatar } from 'antd';
import { DeleteOutlined, EditOutlined, PaperClipOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { getData } from '../../actions/apiCall';
import { postData } from '../../actions/developer';
import moment from 'moment';

interface Note {
    _id: string;
    title: string;
    description?: string;
    refType: string;
    refId: {
        preName?: string;
        firstName?: string;
        lastName?: string;
        _id?: string;
        businessName?: string;
        dealName?: string;
    };
    createdBy: {
        firstName: string;
        lastName: string;
        avatar?: string;
        _id: string;
    };
    createdAt: string;
}

interface NotesComponentProps {
    refType: string; // e.g., 'Lead'
    refId: string;   // the ID of the lead/contact/etc.
}

const NoteItem: React.FC<{ note: Note }> = ({ note }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const descRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = descRef.current;
        if (el && el.scrollHeight > el.clientHeight + 1) {
            setIsOverflowing(true);
        }
    }, []);

    return (
        <div className="group flex justify-between items-start max-w-xl">
            <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-800 flex items-center justify-center font-semibold">
                    <Avatar
                        src={
                            note.createdBy.avatar
                                ? `${process.env.FILE_URL}${note.createdBy.avatar}`
                                : undefined
                        }
                        shape="circle"
                        size="default"
                    >
                        {note.createdBy?.firstName?.charAt(0)}
                    </Avatar>
                </div>
                <div>
                    <div className="text-sm font-semibold">{note.title}</div>

                    {note.description && (
                        <div>
                            <div
                                ref={descRef}
                                className={`text-sm text-gray-700 ${!isExpanded ? 'line-clamp-1' : ''}`}
                                style={{ maxWidth: '36rem' }}
                            >
                                {note.description}
                            </div>
                            {isOverflowing && (
                                <div
                                    className="text-blue-600 text-xs mt-1 cursor-pointer inline-block"
                                    onClick={() => setIsExpanded(!isExpanded)}
                                >
                                    {isExpanded ? 'Show less' : 'Show more'}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="text-xs text-gray-500 mt-1">
                        {note.refType} –{' '}
                        <span className="text-blue-600 cursor-pointer">
                            {note.refId?.businessName || note.refId?.dealName || [note.refId?.preName, note.refId?.firstName, note.refId?.lastName].filter(Boolean).join(' ')}
                        </span>{' '}
                        • {moment(note.createdAt).format('LLL')} by{' '}
                        {note.createdBy.firstName} {note.createdBy.lastName}
                    </div>
                </div>
            </div>
            <Space direction="horizontal" className="group-hover:flex hidden">
                <Button type="text" className="rounded-full border border-gray-300" size="small" icon={<EditOutlined />} />
                <Button type="text" className="rounded-full border border-gray-300" size="small" icon={<DeleteOutlined />} />
            </Space>
        </div>
    );
};

const NotesComponent: React.FC<NotesComponentProps> = ({ refType, refId }) => {
    const [notes, setNotes] = useState<Note[]>([]);
    const [isAdding, setIsAdding] = useState<boolean>(false);
    const [newTitle, setNewTitle] = useState('');
    const [newDescription, setNewDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();

    const fetchNotes = async () => {
        try {
            if (!refId || !refType) {
                message.error('Invalid reference type or ID');
                return;
            }
            setLoading(true);
            const res = await dispatch(getData(`/notes?refType=${refType}&refId=${refId}`));
            if (res.code === 200) {
                setNotes(res.data);
            }
        } catch (error) {
            message.error('Failed to load notes');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!newTitle.trim()) return;

        try {

            const res = await dispatch(postData('/notes', {
                title: newTitle,
                description: newDescription,
                refType,
                refId,
            }))

            setNewTitle('');
            setNewDescription('');
            setIsAdding(false);
            fetchNotes();
            message.success('Note added');
        } catch (error) {
            message.error('Failed to add note');
        }
    };

    useEffect(() => {
        if (!refId || !refType) return;
        fetchNotes();
    }, [refId, refType]);

    return (
        <div className="space-y-4 p-4">
            {notes.map(note => (<NoteItem key={note._id} note={note} />))}
            {isAdding && (
                <div className="border border-blue-300 rounded-lg max-w-xl space-y-2">
                    <div className="p-4">
                        <Input
                            placeholder="Title"
                            className="font-semibold text-base !border-0 !shadow-none !bg-transparent"
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                        />
                        <Input.TextArea
                            placeholder="What's this note about?"
                            autoSize={{ minRows: 2 }}
                            className="!border-0 !shadow-none !bg-transparent"
                            value={newDescription}
                            onChange={(e) => setNewDescription(e.target.value)}
                        />
                    </div>
                    <div className="flex justify-between items-center p-2 bg-gray-50 rounded-bl-lg rounded-br-lg">
                        <Tooltip title="Attach a file">
                            <Button icon={<PaperClipOutlined />} type="text" />
                        </Tooltip>
                        <div className="space-x-2">
                            <Button onClick={() => setIsAdding(false)}>Cancel</Button>
                            <Button type="primary" onClick={handleSave}>
                                Save
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {!isAdding && (
                <div
                    onClick={() => setIsAdding(true)}
                    className="border rounded-lg max-w-xl px-4 py-2 cursor-pointer hover:bg-gray-50 transition"
                >
                    Add note
                </div>
            )}
        </div>
    );
};

export default NotesComponent;
